import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Compass, Loader2 } from "lucide-react";
import { GoogleIcon } from "@/components/GoogleIcon";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", affiliation: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex relative bg-primary text-white flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-50" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <img src="/noventra-logo.png" alt="Noventra Academic Press" className="w-9 h-9 rounded-lg object-cover bg-white" />
          <span className="font-serif font-bold text-lg">Noventra Academic Press</span>
        </Link>
        <div className="relative">
          <h2 className="font-serif font-black text-4xl leading-tight">Join a global <br />research community.</h2>
          <p className="mt-4 text-slate-300 max-w-sm">Create an author account to submit your work for open-access publication.</p>
        </div>
        <p className="relative text-xs text-slate-400 font-mono">© 2026 Noventra Academic Press</p>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100">Create account</h1>
          <p className="text-slate-500 mt-2 text-sm">Register as an author.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5" placeholder="Dr. Jane Smith" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" placeholder="you@institution.edu" />
            </div>
            <div>
              <Label htmlFor="affiliation">Affiliation</Label>
              <Input id="affiliation" data-testid="register-affiliation" value={form.affiliation} onChange={set("affiliation")} className="mt-1.5" placeholder="University / Institute" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} className="mt-1.5" placeholder="At least 6 characters" />
            </div>
            {err && <p data-testid="register-error" className="text-sm text-destructive">{err}</p>}
            <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full rounded-full h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200 dark:border-slate-700" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white dark:bg-slate-900 px-2 text-slate-400">or</span></div>
          </div>
          <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-register-btn" className="w-full rounded-full h-11 gap-2">
            <GoogleIcon /> Continue with Google
          </Button>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Already have an account? <Link to="/login" data-testid="to-login" className="text-primary dark:text-sky-400 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
